/* Progressive enhancement: a failed suggestions request never blocks native search. */
class ColtSearch extends HTMLElement {
  connectedCallback() {
    if (this.events) return;
    this.input = this.querySelector('input[type="search"]');
    this.form = this.querySelector('form');
    this.panel = this.querySelector('.colt-search__panel');
    this.results = this.querySelector('.colt-search__results');
    this.status = this.querySelector('[role="status"]');
    this.clearButton = this.querySelector('[type="reset"]');
    this.disclosure = this.querySelector('details');
    this.toggle = this.querySelector('summary');
    this.events = new AbortController();
    this.cache = new Map();
    this.version = 0;
    const on = (element, event, handler) => element.addEventListener(event, handler, { signal: this.events.signal });
    this.toggle.setAttribute('aria-expanded', String(this.disclosure.open));
    on(this.toggle, 'click', (event) => {
      // Focus in the original click event also opens the keyboard on iOS.
      event.preventDefault();
      if (this.disclosure.open) this.collapse(true);
      else this.expand();
    });
    on(this.disclosure, 'toggle', () => {
      this.toggle.setAttribute('aria-expanded', String(this.disclosure.open));
      this.toggle.setAttribute('aria-label', this.disclosure.open ? this.toggle.dataset.closeLabel : this.toggle.dataset.openLabel);
      if (!this.disclosure.open) this.close();
    });
    on(this.querySelector('.colt-search__backdrop'), 'click', () => this.collapse(true));
    on(this.querySelector('.colt-search__backdrop'), 'pointerdown', (event) => {
      event.preventDefault();
      this.collapse(true);
    });
    if (this.dataset.predictive === 'true') {
      this.input.setAttribute('role', 'combobox');
      this.input.setAttribute('aria-autocomplete', 'list');
      this.input.setAttribute('aria-controls', this.results.id);
      this.input.setAttribute('aria-expanded', 'false');
      this.input.setAttribute('aria-haspopup', 'listbox');
    }
    this.clearButton.hidden = !this.input.value;
    on(this.input, 'input', (event) => {
      this.close();
      this.clearButton.hidden = !this.input.value;
      if (!event.isComposing) this.schedule();
    });
    on(this.input, 'compositionend', () => this.schedule());
    on(this.input, 'focus', () => this.schedule());
    on(this.input, 'keydown', (event) => this.onKeydown(event));
    on(this, 'keydown', (event) => {
      if (event.key === 'Escape' && !event.isComposing) { event.preventDefault(); event.stopPropagation(); this.collapse(true); }
    });
    on(this.form, 'reset', (event) => {
      event.preventDefault();
      this.input.value = '';
      // Move focus before hiding the focused reset button, or the browser blurs
      // to the page and the disclosure's focusout handler closes the search.
      this.input.focus();
      this.clearButton.hidden = true;
      this.close();
    });
    on(this.form, 'submit', (event) => {
      if (!this.input.value.trim()) { event.preventDefault(); this.input.focus(); }
    });
    on(this.querySelector('.colt-search__close'), 'click', () => {
      this.input.focus();
      this.close();
    });
    on(document, 'pointerdown', (event) => { if (!this.contains(event.target)) this.collapse(); });
    // This is a non-modal disclosure: Tab can continue to account/cart and closes search.
    on(this, 'focusout', (event) => { if (!this.contains(event.relatedTarget)) this.collapse(); });
    on(window, 'resize', () => this.measure());
    if (window.visualViewport) on(window.visualViewport, 'resize', () => this.measure());
  }

  disconnectedCallback() {
    this.collapse();
    this.events?.abort();
    this.events = null;
  }

  expand() {
    this.disclosure.open = true;
    this.measure();
    this.toggle.setAttribute('aria-expanded', 'true');
    this.toggle.setAttribute('aria-label', this.toggle.dataset.closeLabel);
    this.input.focus({ preventScroll: true });
  }

  collapse(returnFocus = false) {
    if (!this.disclosure) return;
    const wasOpen = this.disclosure.open;
    this.disclosure.open = false;
    this.toggle.setAttribute('aria-expanded', 'false');
    this.toggle.setAttribute('aria-label', this.toggle.dataset.openLabel);
    this.close();
    if (returnFocus && wasOpen) this.toggle.focus({ preventScroll: true });
  }

  schedule() {
    clearTimeout(this.timer);
    if (this.dataset.predictive !== 'true' || !this.input.value.trim()) return;
    this.timer = setTimeout(() => this.search(this.input.value.trim()), 250);
  }

  async search(query) {
    this.request?.abort();
    this.request = new AbortController();
    const version = ++this.version;
    this.status.textContent = this.dataset.loadingText;
    this.setAttribute('loading', '');
    this.results.setAttribute('aria-busy', 'true');
    try {
      let markup = this.cache.get(query);
      if (markup === undefined) {
        const params = new URLSearchParams({ q: query, 'resources[type]': 'product', 'resources[limit]': '4', section_id: 'colt-predictive-search' });
        const response = await fetch(`${this.dataset.searchUrl}?${params}`, { signal: this.request.signal });
        if (!response.ok) throw new Error(`Search returned ${response.status}`);
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const fragment = doc.querySelector('[data-colt-results]');
        if (!fragment) throw new Error('Missing search results');
        markup = fragment.outerHTML;
        if (this.cache.size >= 20) this.cache.delete(this.cache.keys().next().value);
        this.cache.set(query, markup);
      }
      // Ignore responses after typing, clearing, closing, leaving the field or disconnecting.
      if (version !== this.version || query !== this.input.value.trim() || !this.contains(document.activeElement)) return;
      this.results.innerHTML = markup;
      this.removeAttribute('loading');
      this.results.setAttribute('aria-busy', 'false');
      const fragment = this.results.querySelector('[data-colt-results]');
      this.status.textContent = fragment.dataset.announcement;
      this.input.removeAttribute('aria-activedescendant');
      this.panel.hidden = Number(fragment.dataset.count) === 0;
      this.input.setAttribute('aria-expanded', String(!this.panel.hidden));
      this.measure();
    } catch (error) {
      if (version === this.version) this.close();
    }
  }

  onKeydown(event) {
    if (event.isComposing || this.panel.hidden) return;
    const options = [...this.results.querySelectorAll('[role="option"]')];
    const selected = options.findIndex((option) => option.getAttribute('aria-selected') === 'true');
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (!options.length) return;
      event.preventDefault();
      const next = selected < 0 ? (event.key === 'ArrowDown' ? 0 : options.length - 1) : (selected + (event.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length;
      options.forEach((option, index) => option.setAttribute('aria-selected', String(index === next)));
      this.input.setAttribute('aria-activedescendant', options[next].id);
      options[next].scrollIntoView({ block: 'nearest' });
    } else if (event.key === 'Enter' && selected >= 0) {
      event.preventDefault();
      options[selected].click();
    }
  }

  measure() {
    const headerBottom = this.closest('header')?.getBoundingClientRect().bottom;
    if (headerBottom !== undefined) this.style.setProperty('--colt-search-top', `${headerBottom}px`);
    if (!this.panel || this.panel.hidden) return;
    const viewport = window.visualViewport;
    const bottom = viewport ? viewport.height + viewport.offsetTop : window.innerHeight;
    this.style.setProperty('--colt-search-space', `${Math.max(100, bottom - this.panel.getBoundingClientRect().top - 36)}px`);
  }

  close() {
    clearTimeout(this.timer);
    this.request?.abort();
    this.version++;
    if (!this.panel) return;
    this.panel.hidden = true;
    this.removeAttribute('loading');
    this.results.setAttribute('aria-busy', 'false');
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
    this.results.querySelectorAll('[aria-selected="true"]').forEach((option) => option.setAttribute('aria-selected', 'false'));
    this.status.textContent = '';
  }
}

if (!customElements.get('colt-search')) customElements.define('colt-search', ColtSearch);
